import React, { memo, type ComponentType } from 'react';
import { TASK } from '@atlassian/jira-business-common';
import type { ChildQueryControllerProps } from './types';

type ChildrenControllerProps = {
	expandedItemIssueIds: string[];
	loadingIssueIds?: string[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	setChildrenQueryData: ChildQueryControllerProps<any>['onDataChange'];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ChildQueryController: ComponentType<ChildQueryControllerProps<any>>;
	getHierarchyLevel?: (issueId: string) => number | undefined;
};

/**
 * Create a`ChildQueryController` for every added expanded items, this will fetch the children data for the specified parent
 *
 * We are memoizing this component to make sure this component does not unnecessary re-render
 * Because the way timeline table structured, we need to have the children re-render the parent
 * And the parent will in turn re-render the children again
 *
 * This can create infinite re-render, and to prevent that we memoize the component to make sure it only re-renders when the data needed by children changes
 */
export const ChildrenController = memo<ChildrenControllerProps>( // go/jfe-eslint
	({ expandedItemIssueIds, setChildrenQueryData, ChildQueryController, getHierarchyLevel }) => (
		<>
			{expandedItemIssueIds.map((expandedItemIssueId) => (
				<ChildQueryController
					parentIssueId={expandedItemIssueId}
					parentHierarchyType={TASK}
					parentHierarchyLevel={
						getHierarchyLevel ? getHierarchyLevel(expandedItemIssueId) : undefined
					}
					onDataChange={setChildrenQueryData}
					key={expandedItemIssueId}
				/>
			))}
		</>
	), // Only re-render when the content of expandedItemIssueIds changed or when the setChildrenQueryData changes
	(prevProps, nextProps) => {
		const prevIssueIds = prevProps.expandedItemIssueIds.sort();
		const nextIssueIds = nextProps.expandedItemIssueIds.sort();
		const prevLoadingIssueIds = prevProps.loadingIssueIds?.sort();
		const nextLoadingIssueIds = nextProps.loadingIssueIds?.sort();

		let loadingIssuesMatch = true;
		if (prevLoadingIssueIds === undefined || nextLoadingIssueIds === undefined) {
			loadingIssuesMatch = prevLoadingIssueIds === nextLoadingIssueIds;
		} else {
			loadingIssuesMatch =
				prevLoadingIssueIds?.length === nextLoadingIssueIds?.length &&
				prevLoadingIssueIds.every((_, id) => prevLoadingIssueIds[id] === nextLoadingIssueIds[id]);
		}
		return (
			prevProps.setChildrenQueryData === nextProps.setChildrenQueryData &&
			prevIssueIds.length === nextIssueIds.length &&
			prevIssueIds.every((_, id) => prevIssueIds[id] === nextIssueIds[id]) &&
			loadingIssuesMatch
		);
	},
);
